/* ═══════════════════════════════════════════════════════════════════
   MetricWithExplainer — reusable tooltip wrapper for any wave metric.

   Usage:
     <MetricWithExplainer metric="velocity">
       <span>Velocity +41%</span>
     </MetricWithExplainer>

   Hovering the child shows a polished tooltip card with:
     • Plain-English definition
     • The formula
     • Why it matters (business impact)
   ═══════════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { METRICS } from './waveMetrics';

// Re-exported for back-compat — the new source of truth is waveMetrics.js
export const METRIC_DEFINITIONS = Object.fromEntries(
  Object.entries(METRICS).map(([key, m]) => [key, {
    label: m.label,
    definition: m.definition,
    formula: m.formula,
    impact: m.impact,
  }])
);

export function MetricExplainerIcon({ metric, size = 'sm', placement = 'bottom' }) {
  const def = METRIC_DEFINITIONS[metric];
  const [open, setOpen] = useState(false);
  if (!def) return null;

  const iconSize = size === 'xs' ? 'h-2.5 w-2.5' : 'h-3 w-3';
  const placementClass = placement === 'top'
    ? 'bottom-full mb-2'
    : placement === 'left'
      ? 'right-full mr-2 top-0'
      : 'top-full mt-2';

  return (
    <span className="relative inline-flex items-center ml-1 cursor-help"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          tabIndex={0}
          data-testid={`metric-explainer-${metric}`}>
      <HelpCircle className={`${iconSize} text-gray-600 hover:text-[#00D4FF] transition-colors`} />
      {open && (
        <div className={`absolute ${placementClass} left-1/2 -translate-x-1/2 z-50 w-64 p-3 rounded-xl shadow-2xl pointer-events-none`}
             style={{ background: '#141820', border: '1px solid #2E3240' }}>
          <p className="text-[11px] font-black text-white mb-1.5 leading-tight">{def.label}</p>
          <p className="text-[10px] text-gray-300 leading-snug mb-2">{def.definition}</p>
          <div className="rounded p-1.5 mb-2" style={{ background: '#0a0a0a', border: '1px solid #1E1E1E' }}>
            <p className="text-[8px] uppercase tracking-widest text-gray-500 font-bold mb-0.5">Formula</p>
            <p className="text-[9px] text-[#00D4FF] font-semibold leading-snug">{def.formula}</p>
          </div>
          <p className="text-[9px] text-gray-400 leading-snug italic">{def.impact}</p>
        </div>
      )}
    </span>
  );
}

export default function MetricWithExplainer({ metric, children, placement = 'bottom' }) {
  return (
    <span className="inline-flex items-center">
      {children}
      <MetricExplainerIcon metric={metric} placement={placement} />
    </span>
  );
}
